/**
 * Proposal 0 — SureScripts medication-history consent model (presentational).
 *
 * Mirrors the real backend contract: the unified endpoint (Component D) returns
 * a `consent` object whose state is one of these four values
 * (`surescripts_history_service.ConsentState`). The already-shipped
 * `eprescriptions/MedicationHistoryList` renders the same needed → pulling →
 * granted flow against live data; this proposal reuses its copy so the mock
 * reads identically, without the RTK/server-UI wiring.
 */

import type { HistoryFill } from '../vm/types';

export type ConsentState = 'unavailable' | 'needed' | 'pulling' | 'granted';

// The fill shape lives with the other view-models so the mapper can produce it
// without importing from the component layer.
export type { HistoryFill } from '../vm/types';

// Copy adapted from eprescriptions/MedicationHistoryList (same flow, same
// language), parameterized on the org's subject language ("patient"/"client")
// per the subject-language rule — hosts pass useSubjectLanguageVariants().singular.
export const consentNeededCopy = (subject: string) =>
  `With recorded consent, we can pull this ${subject}’s pharmacy fill history from SureScripts so you can see what other prescribers have written.`;

export const consentCtaLabel = (subject: string) => `Log ${subject} consent to pull history`;

export const PULLING_COPY =
  'Pulling fills from SureScripts… this usually takes a few seconds.';

// `consent_stale`: we hold a consent record but DoseSpot rejected the last
// pull (GDN-3281) — whatever the section shows is not a complete history, and
// re-confirming consent is the only way to resume refreshes.
export const staleRefreshCopy = (subject: string) =>
  `Medication history couldn’t be refreshed — re-confirm ${subject} consent to resume pulling from SureScripts.`;

export const RECONFIRM_CTA_LABEL = 'Re-confirm consent';

export const emptyGrantedCopy = (subject: string) =>
  `No fills found for this ${subject} yet — once SureScripts returns data it'll appear here.`;

// When SureScripts DID return fills but every one matched a charted medication
// (they render inside that medication's history drawer), the plain empty copy
// reads as "no data" — say where the fills went instead.
export const attributedFillsCopy = (subject: string, count: number) =>
  `SureScripts returned ${count} ${count === 1 ? 'fill' : 'fills'} for this ${subject}, all matched to medications listed above — open a medication's past prescriptions to see them.`;

export const unavailableCopy = (subject: string) =>
  `Pharmacy medication history isn’t available — this ${subject} isn’t connected to e-prescribing yet.`;

/**
 * Example fills for the `granted` state. Insurance-adjudicated claims (cash-pay
 * fills don't appear in SureScripts), showing only the fields DoseSpot returns:
 * drug, directions-when-present, quantity, fill date, payer, controlled flag.
 * One fill deliberately has no directions — the real feed is inconsistent there.
 * Proposal-local demo data; intentionally not added to the shared mock.
 */
export const HISTORY_FILL_EXAMPLES: HistoryFill[] = [
  {
    id: 'srx-1',
    drugName: 'Gabapentin 300 mg',
    sig: '1 capsule three times daily',
    quantity: '90 capsules',
    daysSupply: 30,
    lastFillDate: '2026-05-12',
    payer: 'OptumRx',
    indication: 'Neuropathic pain',
  },
  {
    id: 'srx-2',
    drugName: 'Clonazepam 0.5 mg',
    sig: '1 tablet at bedtime as needed',
    quantity: '30 tablets',
    daysSupply: 30,
    lastFillDate: '2026-05-28',
    payer: 'Express Scripts',
    isControlled: true,
  },
  {
    id: 'srx-3',
    drugName: 'Hydroxyzine 25 mg',
    // No directions on this claim — SureScripts often omits them.
    quantity: '60 tablets',
    daysSupply: 30,
    lastFillDate: '2026-03-08',
    payer: 'Caremark',
  },
  {
    id: 'srx-4',
    drugName: 'Amoxicillin 500 mg',
    sig: '1 capsule twice daily × 10 days',
    quantity: '20 capsules',
    daysSupply: 10,
    lastFillDate: '2025-11-20',
    payer: 'Caremark',
  },
];
